import { GridColDef } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Divider, Menu, MenuItem, Stack } from "@mui/material";
import { useTheme } from "../../../../../components/hooks/useTheme";
import baseURL from "../../../../../configs/api";
import { CustomDataGrid } from "../../../../../components/common/CustomDataGrid";
import Cookies from "js-cookie";
import useTenant from "../../../../../components/hooks/useTenant";

export const AllDamageTypes = () => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { themeStyles } = useTheme();
    const { handleTenantError }: any = useTenant();
    const [loading, setLoading] = useState(true);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [tableData, setTableData] = useState<any[]>([]);
    const [totalLength, setTotalLength] = useState<number>(0);
    const [columnFilterAnchorEl, setColumnFilterAnchorEl] = useState<null | HTMLElement>(null);
    const columnFilterOpen = Boolean(columnFilterAnchorEl);
    const initialColumns: GridColDef[] = [
        {
            field: "damageName",
            headerName: "Name",
            flex: 2,
            editable: false,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Link to="/admin/fleet/maintenance/editdamagetype" state={params.id}
                        style={{
                            color: "var(--app-link-color)",
                            textDecoration: "underline",
                        }}
                    >
                        {params.value}
                    </Link>
                );
            },
        },
        {
            field: "isActive",
            headerName: "Active?",
            flex: 1,
            editable: false,
            sortable: false,
        }
    ];
    const [columns, setColumns] = useState<GridColDef[]>(initialColumns);
    const [selectedColumns, setSelectedColumns] = useState(initialColumns?.map(col => col?.headerName));

    const handleColumnFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setColumnFilterAnchorEl(event.currentTarget);
    };
    const handleColumnFilterClose = () => {
        setColumnFilterAnchorEl(null);
    };

    const toggleColumn = (headerName: string) => {
        setSelectedColumns(prev =>
            prev.includes(headerName) ? prev.filter(col => col !== headerName) : [...prev, headerName]
        );
    };

    useEffect(() => {
        const filterCol = initialColumns?.filter(col => selectedColumns?.includes(col?.headerName));
        setColumns(filterCol);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedColumns])

    const toggleAll = () => {
        if (selectedColumns?.length === initialColumns?.length) {
            setSelectedColumns([]);
        } else {
            setSelectedColumns(initialColumns?.map(col => col?.headerName));
        }
    };

    useEffect(() => {
        emptySearchApiCall();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel])

    const emptySearchApiCall = () => {
        setLoading(true);
        fetch(`${baseURL}damageType`, {
            headers: {
                'X-Tenant-ID': tenantId,
            }
        })
            .then((res) => res.json())
            .then((data) => {
                const transformedData = data.map((item: any) => ({
                    ...item,
                    isActive: item.isActive ? "Yes" : "No",
                }));

                setTableData(transformedData);
                setTotalLength(transformedData?.length || 0);
                setLoading(false);
                handleTenantError(null);
            })
            .catch(error => {
                setTableData([]);
                setTotalLength(0);
                setLoading(false);
                handleTenantError(error);
            });
    };

    return (
        <>
            <div className="w-full mt-2">
                <div className={`w-full border ${themeStyles.main.borderColor} rounded-[16px]`}>
                    <div className={`flex justify-between items-center p-4 border-b ${themeStyles.main.borderColor}`}>
                        <div className="flex items-center gap-1.5">

                            <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Damage Types</h2>

                            <div >
                                <button
                                    className={`flex items-center justify-between py-[5px] px-[12px] gap-2.5 rounded-lg border border-solid ${themeStyles.topBar.tailwindBorderColor}`}
                                    aria-controls={columnFilterOpen ? 'basic-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={columnFilterOpen ? 'true' : undefined}
                                    onClick={handleColumnFilterClick}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems={"center"}
                                        justifyContent={"center"}
                                    >
                                        <span className={`${themeStyles.topBar.tailwindTextColor} text-[14px] font-medium`}>Columns</span>
                                        <span className={`material-symbols-outlined text-[18px] font-light ${themeStyles.main.textHeadingColor}`}>
                                            filter_alt
                                        </span>
                                    </Stack>
                                </button>

                                <Menu
                                    id="basic-menu"
                                    anchorEl={columnFilterAnchorEl}
                                    open={columnFilterOpen}
                                    onClose={handleColumnFilterClose}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                width: 170,
                                                maxHeight: 300,
                                                backgroundColor: themeStyles.topBar.bg
                                            }
                                        }
                                    }}
                                >
                                    <div className="flex items-center gap-2 px-[16px] py-[6px] w-full">
                                        <input
                                            type="checkbox"
                                            className="form-checkbox cursor-pointer"
                                            checked={selectedColumns?.length === initialColumns?.length ? true : false}
                                            onClick={toggleAll}
                                        />
                                        <span className={`${themeStyles.topBar.tailwindTextColor} font-medium text-[12px]`}>Select All</span>
                                    </div>
                                    <Divider className="w-full" />
                                    {
                                        initialColumns?.map((column, index) => (
                                            <>
                                                <MenuItem
                                                    key={index}
                                                    sx={{
                                                        fontSize: "12px",
                                                        marginBlock: "0px",
                                                        "&: hover": {
                                                            cursor: "default"
                                                        }
                                                    }}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            className="form-checkbox cursor-pointer"
                                                            checked={selectedColumns.includes(column?.headerName)}
                                                            onChange={() => toggleColumn(column?.headerName || "")}
                                                        />
                                                        <span className={`${themeStyles.topBar.tailwindTextColor} font-medium`}>{column?.headerName}</span>
                                                    </div>

                                                </MenuItem>
                                                <Divider className="w-full" />
                                            </>
                                        ))
                                    }
                                </Menu>
                            </div>
                        </div>
                    </div>

                    <CustomDataGrid
                        filteredRows={tableData}
                        columns={columns?.map(col => ({
                            ...col,
                            headerClassName: themeStyles.table.headerBg,
                        }))}
                        loading={loading}
                        totalLength={totalLength}
                        paginationModel={paginationModel}
                        setPaginationModel={setPaginationModel}
                    />
                </div>
            </div>
        </>
    )
}